import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CRUDList from '../Common/CRUDList';
import DateInput from '../Common/DateInput';
import { ticketSegments, tickets, legs, legsSchedule } from '../../services/api';
import { formatDateTime, formatDate } from '../../utils/dateFormat';
import { ticketStatuses, ticketStatusColors, usageResults, changeByTypes } from '../../constants/travelEnums';
const destTypes = { 10: 'Regular', 20: 'Stopover' };

const DEFAULT_FORM_DATA = {
  replaced_tsegment_id: null,
  tsegment_init_id: null,
  tsegment_chg_num: 0,
  tsegment_ticket_id: '',
  tsegment_leg_id: '',
  tsegment_date: new Date().toISOString().split('T')[0],
  tsegment_dest_type: 10,
  tsegment_status: 10,
  tsegment_change_date: null,
  tsegment_change_by: null,
  tsegment_init_lsch_id: '',
  tsegment_last_lsch_id: '',
  tsegment_usage_result: null,
  prev_change_by: null
};
function SegmentForm({ formData, setFormData, editingId }) {
  const [ticketsList, setTicketsList] = useState([]);
  const [legsList, setLegsList] = useState([]);
  const [instancesList, setInstancesList] = useState([]);
  const [filteredInstances, setFilteredInstances] = useState([]);
  const [segmentsList, setSegmentsList] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingReplacementId, setPendingReplacementId] = useState(null);
  const isReplacement = formData.tsegment_init_id != null;
  const isEditing = editingId != null;

  useEffect(() => {
    loadTickets();
    loadLegs();
    loadInstances();
    loadSegments();
  }, []);

  // When leg selection or instances change, prepare filtered instances list
  useEffect(() => {
    if (formData.tsegment_leg_id) {
      // If selected leg is a marketing (operated) leg, show instances of its operational leg
      const selectedLeg = legsList.find(l => l.leg_id === formData.tsegment_leg_id);
      const targetLegId = selectedLeg && selectedLeg.leg_operational_leg_id ? selectedLeg.leg_operational_leg_id : formData.tsegment_leg_id;
      setFilteredInstances(instancesList.filter(ls => ls.lsch_leg_id === targetLegId && ls.lsch_status === 10));
    } else {
      setFilteredInstances(instancesList.filter(ls => ls.lsch_status === 10));
    }
  }, [formData.tsegment_leg_id, instancesList]);

  const loadTickets = async () => {
    try {
      const response = await tickets.getAll();
      setTicketsList(response.data);
    } catch (err) {
      console.error('Failed to load tickets', err);
    }
  };

  const loadLegs = async () => {
    try {
      const response = await legs.getAll();
      setLegsList(response.data);
    } catch (err) {
      console.error('Failed to load legs', err);
    }
  };

  const loadInstances = async () => {
    try {
      const response = await legsSchedule.getAll();
      setInstancesList(response.data);
    } catch (err) {
      console.error('Failed to load legs schedule', err);
    }
  };

  const loadSegments = async () => {
    try {
      const response = await ticketSegments.getAll();
      setSegmentsList(response.data);
    } catch (err) {
      console.error('Failed to load segments', err);
    }
  };

  const handleReplacementChange = (e) => {
    const replacedId = e.target.value ? parseInt(e.target.value) : null;
    if (replacedId) {
      setPendingReplacementId(replacedId);
      setShowConfirmModal(true);
    } else {
      setFormData({ ...formData, replaced_tsegment_id: null, tsegment_init_id: null, tsegment_chg_num: 0, prev_change_by: null });
    }
  };

  const confirmReplacement = () => {
    const replacedId = pendingReplacementId;
    const replacedSegment = segmentsList.find(s => s.tsegment_id === replacedId);
    const initId = replacedSegment?.tsegment_init_id || replacedId;
    const chainSegments = segmentsList.filter(s =>
      s.tsegment_init_id === initId || s.tsegment_id === initId
    );
    const maxChgNum = Math.max(...chainSegments.map(s => s.tsegment_chg_num || 0));
    setFormData({
      ...formData,
      replaced_tsegment_id: replacedId,
      tsegment_init_id: initId,
      tsegment_chg_num: maxChgNum + 1,
      tsegment_ticket_id: replacedSegment?.tsegment_ticket_id || formData.tsegment_ticket_id,
      tsegment_leg_id: replacedSegment?.tsegment_leg_id || formData.tsegment_leg_id,
      // no sequence field anymore
      tsegment_dest_type: replacedSegment?.tsegment_dest_type || formData.tsegment_dest_type,
      prev_change_by: 10
    });
    setShowConfirmModal(false);
    setPendingReplacementId(null);
  };

  const cancelReplacement = () => {
    setShowConfirmModal(false);
    setPendingReplacementId(null);
  };

  const getSelectedSegmentLabel = () => {
    if (pendingReplacementId) {
      const s = segmentsList.find(s => s.tsegment_id === pendingReplacementId);
      if (s) return `${s.ticket_number || s.booking_pnr || `Ticket ${s.tsegment_ticket_id}`}: ${s.carrier_iata} ${s.flight_number} (${s.dep_iata} → ${s.arr_iata})`;
    }
    return '';
  };

  return (
    <>
      {showConfirmModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Confirm Replacement</h3>
            <p>Are you sure you want to replace segment <strong>{getSelectedSegmentLabel()}</strong>? This action will mark the original segment as "Replaced".</p>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={cancelReplacement}>No</button>
              <button type="button" className="btn btn-danger" onClick={confirmReplacement}>Yes</button>
            </div>
          </div>
        </div>
      )}

      {!formData.tsegment_id && (
        <div className="form-group replacement-warning">
          <label>Original Segment (for replacement)</label>
          <select
            value={formData.replaced_tsegment_id || ''}
            onChange={handleReplacementChange}
          >
            <option value="">None (original segment)</option>
            {segmentsList
              .filter(s => s.tsegment_status === 10)
              .map(s => (
                <option key={s.tsegment_id} value={s.tsegment_id}>
                  {s.ticket_number || s.booking_pnr || `Ticket ${s.tsegment_ticket_id}`}: {s.carrier_iata} {s.flight_number} ({s.dep_iata} → {s.arr_iata})
                </option>
              ))}
          </select>
          {isReplacement && (
            <small className="text-muted">Change #: {formData.tsegment_chg_num}</small>
          )}
        </div>
      )}

      {isReplacement && !formData.tsegment_id && (
        <div className="form-group">
          <label>Changed By *</label>
          <select
            value={formData.prev_change_by || ''}
            onChange={(e) => setFormData({ ...formData, prev_change_by: parseInt(e.target.value) })}
            required
          >
            <option value="10">Carrier</option>
            <option value="20">Passenger</option>
          </select>
        </div>
      )}

      <div className="form-group">
        <label>Ticket *</label>
        <select
          value={formData.tsegment_ticket_id || ''}
          onChange={(e) => {
            const ticketId = e.target.value ? parseInt(e.target.value) : null;
            if (ticketId) {
              const ticket = ticketsList.find(t => t.ticket_id === ticketId);
              const ticketDate = ticket?.ticket_date || DEFAULT_FORM_DATA.tsegment_date;
              setFormData({ ...formData, tsegment_ticket_id: ticketId, tsegment_date: ticketDate });
            } else {
              setFormData({ ...formData, tsegment_ticket_id: null, tsegment_date: DEFAULT_FORM_DATA.tsegment_date });
            }
          }}
          required
        >
          <option value="">Select ticket</option>
          {ticketsList
            .filter(t => t.ticket_status === 10)
            .map(t => (
              <option key={t.ticket_id} value={t.ticket_id}>
                {t.ticket_number || `Ticket ${t.ticket_id}`} - {t.booking_pnr}
              </option>
            ))}
        </select>
      </div>

      <div className="form-group">
        <label>Segment Date</label>
        <DateInput
          value={formData.tsegment_date || ''}
          onChange={(value) => setFormData({ ...formData, tsegment_date: value })}
        />
      </div>

      <div className="form-group">
        <label>Leg *</label>
        <select
          value={formData.tsegment_leg_id || ''}
          onChange={(e) => {
            const legId = e.target.value ? parseInt(e.target.value) : null;
            // clear selected leg instance when changing leg to avoid mismatch
            if (legId) {
              setFormData({ ...formData, tsegment_leg_id: legId, tsegment_init_lsch_id: '', tsegment_last_lsch_id: '' });
            } else {
              setFormData({ ...formData, tsegment_leg_id: null, tsegment_init_lsch_id: '', tsegment_last_lsch_id: '' });
            }
          }}
          required
        >
          <option value="">Select leg</option>
          {legsList.map(l => (
            <option key={l.leg_id} value={l.leg_id}>
              {l.carrier_iata || l.carrier_name} {l.flight_number}: {l.dep_iata} → {l.arr_iata}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Leg Instance {!isEditing && '*'}</label>
        {isEditing ? (
          // Edit mode - show read-only info
          (() => {
            const currentInstance = instancesList.find(ls => ls.lsch_id === formData.tsegment_last_lsch_id);
            return (
              <div style={{
                padding: '8px 12px',
                background: '#f8f9fa',
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                color: '#555'
              }}>
                {currentInstance ? (
                  <>
                    <strong>{currentInstance.carrier_iata || currentInstance.carrier_name} {currentInstance.flight_number}</strong>: {currentInstance.dep_iata} → {currentInstance.arr_iata}
                    <br />
                    <span style={{ fontSize: '0.9em', color: '#7f8c8d' }}>
                      Dep: {formatDateTime(currentInstance.lsch_dep_date, currentInstance.lsch_dep_time)}, Arr: {formatDateTime(currentInstance.lsch_arr_date, currentInstance.lsch_arr_time)}
                    </span>
                  </>
                ) : (
                  <span style={{ color: '#999' }}>Schedule entry not found (ID: {formData.tsegment_last_lsch_id})</span>
                )}
              </div>
            );
          })()
        ) : (
          // Create mode - show dropdown
          <select
            value={formData.tsegment_init_lsch_id || ''}
            onChange={(e) => {
              const lschId = parseInt(e.target.value);
              setFormData({
                ...formData,
                tsegment_init_lsch_id: lschId,
                tsegment_last_lsch_id: lschId
              });
            }}
            required
          >
            <option value="">Select schedule entry</option>
            {filteredInstances.map(ls => (
              <option key={ls.lsch_id} value={ls.lsch_id}>
                {ls.carrier_iata || ls.carrier_name} {ls.flight_number}: {ls.dep_iata} → {ls.arr_iata} (Dep: {formatDateTime(ls.lsch_dep_date, ls.lsch_dep_time)}, Arr: {formatDateTime(ls.lsch_arr_date, ls.lsch_arr_time)})
              </option>
            ))}
          </select>
        )}
      </div>

      <div className="form-group">
        <label>Destination Type *</label>
        <select
          value={formData.tsegment_dest_type || ''}
          onChange={(e) => setFormData({ ...formData, tsegment_dest_type: parseInt(e.target.value) })}
          required
        >
          <option value="">Select type</option>
          <option value="10">Regular</option>
          <option value="20">Stopover (no exit)</option>
        </select>
      </div>

      <div className="form-group">
        <label>Status *</label>
        <select
          value={formData.tsegment_status || ''}
          onChange={(e) => setFormData({ ...formData, tsegment_status: parseInt(e.target.value) })}
          required
        >
          <option value="">Select status</option>
          <option value="10">Active</option>
          <option value="20">Replaced</option>
          <option value="30">Cancelled</option>
        </select>
      </div>

      {formData.tsegment_status === 30 && (
        <>
          <div className="form-group">
            <label>Change Date</label>
            <DateInput
              value={formData.tsegment_change_date || ''}
              onChange={(value) => setFormData({ ...formData, tsegment_change_date: value })}
            />
          </div>
          <div className="form-group">
            <label>Cancelled By</label>
            <select
              value={formData.tsegment_change_by || ''}
              onChange={(e) => setFormData({ ...formData, tsegment_change_by: e.target.value ? parseInt(e.target.value) : null })}
            >
              <option value="">Select who cancelled</option>
              {Object.entries(changeByTypes).map(([key, value]) => (
                <option key={key} value={key}>{value}</option>
              ))}
            </select>
          </div>
        </>
      )}

      {formData.tsegment_status === 10 && (
        <div className="form-group">
          <label>Usage Result</label>
          <select
            value={formData.tsegment_usage_result || ''}
            onChange={(e) => setFormData({ ...formData, tsegment_usage_result: e.target.value ? parseInt(e.target.value) : null })}
          >
            <option value="">Not yet used</option>
            {Object.entries(usageResults).map(([key, value]) => (
              <option key={key} value={key}>{value}</option>
            ))}
          </select>
        </div>
      )}
    </>
  );
}

function TicketSegmentsList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ ...DEFAULT_FORM_DATA });
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('10_planned');
  const [legsList, setLegsList] = useState([]);
  const [noShowConfirmId, setNoShowConfirmId] = useState(null);

  // DEFAULT_FORM_DATA is declared at module scope

  useEffect(() => {
    loadItems();
    loadLegs();
  }, []);

  const loadItems = async () => {
    try {
      setLoading(true);
      const response = await ticketSegments.getAll();
      // Sort by departure date-time ascending
      const sorted = [...response.data].sort((a, b) => {
        const dateA = `${a.lsch_dep_date || ''}T${a.lsch_dep_time || ''}`;
        const dateB = `${b.lsch_dep_date || ''}T${b.lsch_dep_time || ''}`;
        return dateA.localeCompare(dateB);
      });
      setItems(sorted);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadLegs = async () => {
    try {
      const res = await legs.getAll();
      setLegsList(res.data);
    } catch (err) {
      console.error('Failed to load legs', err);
    }
  };

  const handleCreate = () => {
    setFormData(DEFAULT_FORM_DATA);
    setEditingId(null);
    setShowForm(true);
  };

  const handleEdit = (item) => {
    const editData = {};
    Object.keys(DEFAULT_FORM_DATA).forEach(key => {
      let value = item[key] !== undefined ? item[key] : DEFAULT_FORM_DATA[key];
      if (key.includes('_date') && value && typeof value === 'string' && value.includes('T')) {
        value = value.split('T')[0];
      }
      if (key.includes('_time') && value && typeof value === 'string' && value.length > 5) {
        value = value.substring(0, 5);
      }
      editData[key] = value;
    });
    setFormData(editData);
    setEditingId(item.tsegment_id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      await ticketSegments.delete(id);
      loadItems();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete item');
    }
  };

  const handleUpdateUsageResult = async (id, usageResult) => {
    try {
      const item = items.find(i => i.tsegment_id === id);
      if (!item) return;
      
      await ticketSegments.update(id, {
        ...item,
        tsegment_usage_result: usageResult
      });
      loadItems();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update segment');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      // Validation: check that selected leg instance belongs to the chosen leg.
      // If the chosen leg is a marketing (operated) leg, accept instances belonging to its operational leg.
      if (!editingId && formData.tsegment_init_lsch_id) {
        try {
          const res = await legsSchedule.getById(formData.tsegment_init_lsch_id);
          const scheduleEntry = res.data;

          // Determine the effective leg id: if selected leg is marketing, fetch the leg and use its operational leg id
          let targetLegId = formData.tsegment_leg_id;
          if (formData.tsegment_leg_id) {
            try {
              const legRes = await legs.getById(formData.tsegment_leg_id);
              const selectedLeg = legRes.data;
              if (selectedLeg && selectedLeg.leg_operational_leg_id) {
                targetLegId = selectedLeg.leg_operational_leg_id;
              }
            } catch (err) {
              setError('Failed to validate selected Leg');
              return;
            }
          }

          if (scheduleEntry.lsch_leg_id !== targetLegId) {
            setError('Selected Schedule Entry does not belong to the chosen Leg. Please select a matching entry.');
            return;
          }
        } catch (err) {
          setError('Failed to validate selected Schedule Entry');
          return;
        }
      }
      if (editingId) {
        await ticketSegments.update(editingId, formData);
      } else {
        await ticketSegments.create(formData);
      }
      setShowForm(false);
      loadItems();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save item');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setFormData(DEFAULT_FORM_DATA);
    setEditingId(null);
    setError('');
  };

  const filteredItems = (() => {
    let base = items;
    if (statusFilter === '10_planned') {
      base = base.filter(item => item.tsegment_status === 10 && !item.tsegment_usage_result);
    } else if (statusFilter === '10_completed') {
      base = base.filter(item => item.tsegment_status === 10 && item.tsegment_usage_result);
    } else if (statusFilter !== 'all') {
      base = base.filter(item => item.tsegment_status === parseInt(statusFilter));
    }

    const toKey = (item) => `${item.lsch_dep_date || ''}T${item.lsch_dep_time || ''}`;
    return [...base].sort((a, b) => {
      const ka = toKey(a);
      const kb = toKey(b);
      return statusFilter === '10_planned' ? ka.localeCompare(kb) : kb.localeCompare(ka);
    });
  })();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (showForm) {
    return (
      <div>
        <div className="page-header">
          <h2>{editingId ? 'Edit' : 'Create'} Ticket Segment</h2>
        </div>
        {error && <div className="error">{error}</div>}
        <form onSubmit={handleSubmit} className="form-container">
          <SegmentForm formData={formData} setFormData={setFormData} editingId={editingId} />
          <div className="form-actions">
            <button type="submit" className="btn btn-success">Save</button>
            <button type="button" className="btn btn-secondary" onClick={handleCancel}>Cancel</button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h2>Ticket Segments</h2>
        <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontWeight: '600', color: '#2c3e50', margin: 0 }}>Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #ddd' }}
            >
              <option value="all">All</option>
              <option value="10_planned">Confirmed (planned)</option>
              <option value="10_completed">Confirmed (completed)</option>
              <option value="20">Replaced</option>
              <option value="30">Cancelled</option>
            </select>
          </div>
          <button className="btn btn-primary" onClick={handleCreate}>Add New</button>
        </div>
      </div>

      {error && <div className="error">{error}</div>}

      {filteredItems.length === 0 ? (
        <div className="empty-state">
          <h3>No items found</h3>
          <p>{statusFilter === 'all' ? 'Click "Add New" to create your first ticket segment' : 'No ticket segments with this status'}</p>
        </div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Ticket</th>
                <th>Booking</th>
                <th>Flight</th>
                <th>Route</th>
                <th>Date</th>
                <th style={{ textAlign: 'center' }}>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => {
                let statusTooltip = '';
                const changeByText = item.tsegment_change_by ? changeByTypes[item.tsegment_change_by] : null;
                if (item.tsegment_status === 20 && item.tsegment_change_date) {
                  statusTooltip = `Replaced on ${formatDate(item.tsegment_change_date)}${changeByText ? ` by ${changeByText}` : ''}`;
                } else if (item.tsegment_status === 30 && item.tsegment_change_date) {
                  statusTooltip = `Cancelled on ${formatDate(item.tsegment_change_date)}${changeByText ? ` by ${changeByText}` : ''}`;
                }

                return (
                  <tr key={item.tsegment_id}>
                    <td>{item.tsegment_id}</td>
                    <td>{item.ticket_number || item.booking_pnr || `Ticket ${item.tsegment_ticket_id}`}</td>
                    <td>
                      {item.booking_id ? (
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => navigate(`/booking-details/${item.root_booking_id || item.booking_init_id || item.booking_id}`)}
                          style={{ padding: '4px 8px', fontSize: '0.85em' }}
                        >
                          View
                        </button>
                      ) : (
                        <span style={{ color: '#7f8c8d' }}>—</span>
                      )}
                    </td>
                    <td>
                      <div>
                        <strong>{item.carrier_iata || item.carrier_name} {item.flight_number}</strong>
                        {(() => {
                          const leg = legsList.find(l => l.leg_id === item.tsegment_leg_id);
                          if (leg && leg.leg_operational_leg_id) {
                            const op = legsList.find(l2 => l2.leg_id === leg.leg_operational_leg_id);
                            if (op) {
                              return (
                                <div style={{ fontSize: '0.85em', color: '#7f8c8d', marginTop: '4px' }}>
                                  Operated as {op.carrier_iata || op.carrier_name} {op.flight_number}
                                </div>
                              );
                            }
                          }
                          return null;
                        })()}
                      </div>
                    </td>
                    <td>{item.dep_iata} → {item.arr_iata}</td>
                    <td>{formatDateTime(item.lsch_dep_date, item.lsch_dep_time)}</td>
                    <td style={{ textAlign: 'center' }}>
                      <span
                        className={`badge ${ticketStatusColors[item.tsegment_status]}`}
                        title={statusTooltip}
                        style={{ cursor: statusTooltip ? 'help' : 'default' }}
                      >
                        {ticketStatuses[item.tsegment_status]}
                      </span>
                      {item.tsegment_status === 10 && item.tsegment_usage_result && (
                        <div style={{ marginTop: '4px' }}>
                          <span className={`badge ${item.tsegment_usage_result === 10 ? 'badge-info' : 'badge-secondary'}`}>
                            {usageResults[item.tsegment_usage_result]}
                          </span>
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="actions">
                        <button className="btn btn-primary" onClick={() => handleEdit(item)}>Edit</button>
                        <button className="btn btn-danger" onClick={() => handleDelete(item.tsegment_id)}>Delete</button>
                        {item.tsegment_status === 10 && !item.tsegment_usage_result && (
                          <>
                            <button 
                              className="btn btn-success" 
                              onClick={() => handleUpdateUsageResult(item.tsegment_id, 10)}
                            >
                              Used
                            </button>
                            <button 
                              className="btn btn-warning" 
                              onClick={() => setNoShowConfirmId(item.tsegment_id)}
                            >
                              No-show
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* No-show confirmation modal */}
      {noShowConfirmId && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }} onClick={() => setNoShowConfirmId(null)}>
          <div style={{
            background: 'white',
            padding: '20px',
            borderRadius: '8px',
            maxWidth: '400px',
            width: '90%'
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Confirm No-show</h3>
            <p>Are you sure you want to mark this segment as "No-show"? This action cannot be easily reversed.</p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button 
                className="btn btn-secondary" 
                onClick={() => setNoShowConfirmId(null)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-danger" 
                onClick={() => {
                  handleUpdateUsageResult(noShowConfirmId, 20);
                  setNoShowConfirmId(null);
                }}
              >
                Confirm No-show
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TicketSegmentsList;
